import React, { useEffect, useState } from "react";
import {
  Modal,
  Select,
  DatePicker,
  Input,
  InputNumber,
  Upload,
  Avatar,
  Button,
  Row,
  Col,
  Icon,
  message
} from "antd";
import moment from "moment";
import { renderNotification } from "../../../components/orders/OrderTypeIcons";
import { getOsdReasons, SaveOsdDetails, DeleteOsdDetails } from "../../../api/OrdersApi";
import { randomUUID } from "../../../common/Common";
import I18n from "../../../common/I18n";
import Button_v2 from "../Button_v2";

const { Option, OptGroup } = Select;
const { TextArea } = Input;

const AddOSDModal = ({
  visible,
  onCancel,
  currentOrder,
  warehouses = [],
  dtiversData = [],
  currentOrg,
  refreshDetails,
  editingRecord = null,
  editingOsdIndex = null,
}) => {
  const isEdit = !!editingRecord;
  const [rows, setRows] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsz, setErrorMsz] = useState([]);
  const [trackApicall, setTrackApiCall] = useState(false);

  const orderLabelCode = currentOrder?.label_code;

  const legLocationTypes = [...new Set((currentOrder?.locations || []).map(
    loc => String(loc?.type_of_loc || "").toUpperCase()
  ))];
  const hasPickupLoc = legLocationTypes.includes("PICKUP");
  const hasDeliveryLoc = legLocationTypes.includes("DELIVERY");
  const derivedLegs = [
    ...(hasPickupLoc ? ["Pickup"] : []),
    ...(hasDeliveryLoc ? ["Delivery"] : []),
  ];
  const legOptions = orderLabelCode === "PLH"
    ? ["Pickup", "Linehaul"]
    : orderLabelCode === "LHD"
      ? ["Linehaul", "Delivery"]
      : orderLabelCode === "PLHD"
        ? ["Pickup", "Delivery"]
        : derivedLegs.length ? derivedLegs : ["Pickup", "Delivery"];
  const defaultLeg = legOptions[0];

  const getWhCode = (wh) => wh?.location_code || wh?.code || wh?.warehouse_code || wh?.name || "";
  const orderWarehouseId =
    currentOrder?.customer_order_details?.warehouse_id ||
    currentOrder?.warehouse_id ||
    currentOrder?.wh_location_id;
  const orderWarehouseCode =
    getWhCode(warehouses.find(wh => String(wh.id) === String(orderWarehouseId))) ||
    getWhCode(warehouses?.[0]) ||
    "";

  const createRowFromRecord = (record) => {
    const reasons = record.osd_reason ? record.osd_reason.split(', ').filter(Boolean) : [];
    // Try to find warehouse by warehouse_id first, then by warehouse_code
    const recordWarehouse = warehouses.find(wh => 
      String(wh.id) === String(record.warehouse_id) || 
      getWhCode(wh) === record.warehouse_code
    );
    const terminalCode = recordWarehouse ? getWhCode(recordWarehouse) : (record.warehouse_code || "");
    const existingImages = Array.isArray(record.images) && record.images.length
      ? record.images
      : (record.picture_url ? [{ id: record.id, file_name: record.file_name, picture_url: record.picture_url }] : []);
    const fileList = existingImages.map((img, i) => ({
      uid: img.id || `existing-${i}`,
      name: img.file_name || img.picture_url || `image-${i + 1}`,
      status: "done",
      url: img.picture_url,
      thumbUrl: img.picture_url,
      osdId: img.id || "",
      ackId: img.ack_id,
      imageType: img.image_type || "normal"
    }));
    return {
      id: record.id,
      osdReason: reasons.length > 0 ? reasons : undefined,
      reportedDate: record.reported_date
        ? moment(record.reported_date, "MM/DD/YYYY hh:mm A")
        : moment(),
      quantity: record.qty || 1,
      packagingType: record.packaging_type || "Pieces",
      status: record.status || undefined,
      terminal: terminalCode,
      item: undefined,
      itemName: record.item_name || undefined,
      lpn: record.lpn || undefined,
      reportedBy: record.authorized_by || undefined,
      leg: record.leg || "Pickup",
      notes: record.notes || "",
      docType: "Private",
      fileList
    };
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };


  const createEmptyRow = () => {
    return {
      osdReason: undefined,
      reportedDate: moment(),
      quantity: 1,
      packagingType: "Pieces",
      status: undefined,
      terminal: orderWarehouseCode,
      item: undefined,
      itemName: undefined,
      lpn: undefined,
      reportedBy: undefined,
      leg: defaultLeg,
      notes: "",
      docType: "Private",
      fileList: []
    };
  };

  useEffect(() => {
    if (visible) {
      if (isEdit && editingRecord) {
        setRows([createRowFromRecord(editingRecord)]);
      } else {
        setRows([createEmptyRow()]);
      }
      setErrorMsz([]);
      setTrackApiCall(false);
      fetchOsdReasons();
    }
  }, [visible]);

  const fetchOsdReasons = async () => {
    try {
      const res = await getOsdReasons(currentOrder.id);
      if (res?.data) setReasons(res.data);
      if (res?.users) setUsers(res.users);
      if (res?.items) setItems(res.items);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!visible || !isEdit || !editingRecord || items.length === 0) return;
    const matched = items.find(item => item.item_name === editingRecord.item_name);
    if (matched) {
      setRows(prev => prev.map((r) => {
        if (r.id !== editingRecord.id) return r;
        return { ...r, item: matched.order_item_id, itemName: matched.item_name, lpn: r.lpn || (editingRecord.lpn || undefined) };
      }));
    }
  }, [items, visible, isEdit, editingRecord]);

  const handleChangeRow = (index, field, value) => {
    setRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const handleAddRow = () => {
    setRows(prev => [...prev, createEmptyRow()]);
  };

  const handleCancel = async () => {
    setErrorMsz([]);
    onCancel();
    if (trackApicall) {
      refreshDetails();
    }
  };

  const removeRow = async (index) => {
    const rowToDelete = rows[index];
    if (rowToDelete?.id) {
      await DeleteOsdDetails(currentOrder?.id, rowToDelete.id, currentOrder?.locations?.[0]?.id).then((res) => {
        if (res?.success) setTrackApiCall(true);
        if (rows.length === 1) {
          refreshDetails();
          onCancel();
        }
        if (res?.success === false) {
          renderNotification(res?.errors?.[0] || "Failed to delete OSD details.");
          return;
        }
      });
      message.success("Deleted successfully");
    }
    setRows((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.length ? updated : [createEmptyRow()];
    });
  };

  const handleSave = async () => {
    const errors = [];
    const hasReasonError = rows.some((r) => !r.osdReason || (Array.isArray(r.osdReason) && r.osdReason.length === 0));
    if (hasReasonError) errors.push("OSD Reason cannot be blank");
    const hasAuthError = rows.some((r) => r.status === "CLOSED" && !r.reportedBy);
    if (hasAuthError) errors.push("Authorized By cannot be blank");
    if (errors.length > 0) {
      setErrorMsz(errors);
      renderNotification(errors.join(", "));
      return;
    }
    setErrorMsz([]);
    setLoading(true);
    try {
      const payload = rows.map(r => {
        const reasonArray = Array.isArray(r.osdReason) ? r.osdReason : (r.osdReason ? [r.osdReason] : []);
        const reasonStr = reasonArray.join(", ");
        const codeStr = reasonArray.map(reasonDesc =>
          reasons.find((reason) => reason.description === reasonDesc)?.code || ""
        ).filter(Boolean).join(",");
        const allFiles = r.fileList || [];
        const newFiles = allFiles.map(f => f.originFileObj).filter(Boolean);
        const filesMeta = allFiles.map(f => ({
          id: f.osdId || "",
          name: f.name || "",
          ack_id: f.ackId || `${randomUUID()}_n`,
          file_type: (f.originFileObj ? f.originFileObj.type : f.type) || ""
        }));
        const locations = currentOrder?.locations || [];
        const legType = String(r.leg || "").toUpperCase();
        const legLocation = locations.find(loc => loc.type_of_loc && loc.type_of_loc.toUpperCase() === legType);
        const linehaulTerminalType = orderLabelCode === "PLH" ? "DELIVERY" : orderLabelCode === "LHD" ? "PICKUP" : "";
        const linehaulTerminal = linehaulTerminalType
          ? locations.find(loc => loc.type_of_loc && loc.type_of_loc.toUpperCase() === linehaulTerminalType)
          : null;
        const selectedLocation = legLocation || (legType === "LINEHAUL" ? linehaulTerminal : null) || locations?.[0];
        return {
          ...(r.id ? { id: r.id } : {}),
          order_id: currentOrder?.id,
          location_id: selectedLocation?.id,
          osd_reason: reasonStr,
          osd_code: codeStr,
          qty: r.quantity,
          packaging_type: r.packagingType,
          warehouse_id: warehouses.find(wh => getWhCode(wh) === r.terminal)?.id || r.terminal,
          leg: r.leg,
          status: r.status,
          notes: r.notes,
          item_name: r.itemName || "",
          lpn: r.lpn || "",
          files: newFiles,
          files_meta: JSON.stringify(filesMeta),
          captured_at: r.reportedDate ? r.reportedDate.toISOString() : moment().toISOString(),
          authorized_by: r.status === "CLOSED" ? r.reportedBy : ""
        };
      });

      const res = await SaveOsdDetails(currentOrder?.id, payload);
      if (res?.success === false) {
        renderNotification(res?.errors?.[0] || "Failed to save OSD details", "error");
        setErrorMsz(res?.errors?.[0] || "Failed to save OSD details");
        return;
      }
      message.success("Data saved successfully");
      setTrackApiCall(true);
      if (refreshDetails) refreshDetails();
      onCancel();
    } catch (e) {
      console.error(e);
      renderNotification("Error saving OSD details", "error");
    } finally {
      setLoading(false);
    }
  };

  const packagingOptions = [
    "Pieces", "Handling Units", "Cartons", "Pallets", "Skids", "Cases", "Crates", "Drums", "ULDs", "Boxes", "Air Trays"
  ];

  const statusOptions = ["OPEN", "REVIEW", "CLAIM", "CLOSED"];
  const itemsList = currentOrder?.order_items || currentOrder?.items || [];
  const reportedByList = users?.length ? users : (dtiversData?.length ? dtiversData : []);

  const terminalOptions = warehouses?.length > 0
    ? warehouses.map(wh => getWhCode(wh) || "")
    : [""];

  return (
    <Modal
      className="custom-modal"
      title={isEdit ? `Edit OSD #${editingOsdIndex + 1} Details` : "Add OSD"}
      visible={visible}
      destroyOnClose={false}
      onCancel={handleCancel}
      closable={false}
      maskClosable={false}
      width={850}
      bodyStyle={{ padding: "20px 24px",borderRadius:"20px" }}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button
            onClick={handleAddRow}
            style={{
              borderRadius: "8px",
            }}
              // fontWeight: 500,
              // border: "1px solid #d1d5db",
              // color: "#374151"
          >
            Add Another
          </Button>
          <div>
            <Button_v2
              onClick={handleCancel}
              style={{
                borderRadius: "8px",
              }}
              //   fontWeight: 500,
              //   border: "1px solid #d1d5db",
              //   color: "#374151",
              //   marginRight: "10px"
            >
              Cancel
            </Button_v2>
            <Button_v2
              type="primary"
              onClick={handleSave}
              loading={loading}
              style={{
                borderRadius: "8px",
              }}
              //   fontWeight: 500,
              //   backgroundColor: "#586fcc",
              //   borderColor: "#586fcc",
              //   padding: "0 24px"
            >
              {isEdit ? "Update" : "Save"}
            </Button_v2>
          </div>
        </div>
      }
    >
      {rows.map((row, index) => (
        <div
          key={row.id || index}
          style={{
            backgroundColor: "#f2f6f9",
            borderRadius: "12px",
            border: "1px solid #CDD8E4",
            padding: "20px",
            marginBottom: index < rows.length - 1 ? "16px" : "0px"
          }}
        >
          <Row type="flex" justify="end">
            <Col>
              {index > 0 && (
                <Button
                  type="danger"
                  icon="delete"
                  onClick={() => removeRow(index)}
                  size="small"
                  // style={{ marginLeft: "12px" }}
                />
              )}
            </Col>
          </Row>
          {/* Row 1: OSD Reason, Reported Date */}
          <Row type="flex" justify="space-between" style={{ marginBottom: "16px" }}>
            <Col
              span={12}
              className="osd-reason-select"
            >
              <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "#1A354D", marginBottom: "4px" }}>
                OSD Reason <span style={{ color: "red" }}>*</span>
              </label>
              <Select
              
                mode="tags"
                style={{ width: "100%" }}
                placeholder="Select OSD Reason"
                value={row.osdReason}
                onChange={val => handleChangeRow(index, "osdReason", val)}
                maxTagCount="responsive"
              >
                {reasons.map(r => (
                  <Option key={r.code || r.description} value={r.description || r.code}>
                    {r.description || r.code}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={11}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "#1A354D", marginBottom: "4px" }}>
                Reported Date
              </label>
              <DatePicker
                showTime
                format="MM/DD/YYYY hh:mm A"
                style={{ width: "100%" }}
                value={row.reportedDate}
                onChange={val => handleChangeRow(index, "reportedDate", val)}
              />
            </Col>
          </Row>

          {/* Row 2: Quantity, Packaging Type, Status, Terminal */}
          <Row gutter={16} style={{ marginBottom: "16px" }}>
            <Col span={6}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "#1A354D", marginBottom: "4px" }}>
                Quantity
              </label>
              <InputNumber
                min={1}
                style={{ width: "100%" }}
                value={row.quantity}
                onChange={val => handleChangeRow(index, "quantity", val)}
              />
            </Col>
            <Col span={6}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "#1A354D", marginBottom: "4px" }}>
                Packaging Type
              </label>
              <Select
                style={{ width: "100%" }}
                value={row.packagingType}
                onChange={val => handleChangeRow(index, "packagingType", val)}
              >
                {packagingOptions.map(pkg => (
                  <Option key={pkg} value={pkg}>{pkg}</Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "#1A354D", marginBottom: "4px" }}>
                Status
              </label>
              <Select
                style={{ width: "100%" }}
                value={row.status}
                onChange={val => handleChangeRow(index, "status", val)}
              >
                {statusOptions.map(st => (
                  <Option key={st} value={st}>{st}</Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "#1A354D", marginBottom: "4px" }}>
                {I18n.t("general.warehouse")}
              </label>
              <Select
                style={{ width: "100%" }}
                value={row.terminal}
                onChange={val => handleChangeRow(index, "terminal", val)}
              >
                {terminalOptions.map(t => (
                  <Option key={t} value={t}>{t}</Option>
                ))}
              </Select>
            </Col>
          </Row>

          {/* Row 3: Item, LPN, Reported By, Leg */}
          <Row gutter={16} style={{ marginBottom: "16px" }}>
            <Col span={6}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "#1A354D", marginBottom: "4px" }}>
                Item
              </label>
              <Select
                style={{ width: "100%" }}
                placeholder="Select Item"
                value={row.item}
                onChange={val => {
                  const selected = items.find(item => item.order_item_id === val);
                  handleChangeRow(index, "item", val);
                  handleChangeRow(index, "itemName", selected?.item_name);
                  handleChangeRow(index, "lpn", undefined);
                }}
                allowClear
              >
                {items.map((item, i) => (
                  <Option key={item.order_item_id} value={item.order_item_id}>
                    {item.item_name }
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "#1A354D", marginBottom: "4px" }}>
                LPN
              </label>
              <Select
                style={{ width: "100%" }}
                placeholder="Select LPN"
                value={row.lpn}
                onChange={val => handleChangeRow(index, "lpn", val)}
                allowClear
              >
                {items?.find(item => item.order_item_id === row.item)?.lpns?.map((lpnItem, i) => (
                  <Option key={lpnItem} value={lpnItem}>
                    {lpnItem}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "#1A354D", marginBottom: "4px" }}>
                Reported By {row.status === "CLOSED" && <span style={{ color: "red" }}>*</span>}
              </label>
              <Select
                style={{ width: "100%" }}
                placeholder="Select User"
                value={row.status === "CLOSED" ? row.reportedBy : undefined}
                onChange={val => handleChangeRow(index, "reportedBy", val)}
                disabled={row.status !== "CLOSED"}
                allowClear
              >
                {reportedByList.map(usr => {
                  const name = usr.full_name || usr.name || usr.username || "John S";
                  return (
                    <Option key={usr.id || name} value={name}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Avatar
                          size="small"
                          style={{
                            backgroundColor: "#2563eb",
                            fontSize: "11px",
                            fontWeight: 600,
                            marginRight: "6px",
                            width: "20px",
                            height: "20px",
                            lineHeight: "20px"
                          }}
                        >
                          {getInitials(name)}
                        </Avatar>
                        <span>{name}</span>
                      </div>
                    </Option>
                  );
                })}
              </Select>
            </Col>
            <Col span={6}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "#1A354D", marginBottom: "4px" }}>
                Leg
              </label>
              <Select
                style={{ width: "100%" }}
                value={row.leg}
                onChange={val => handleChangeRow(index, "leg", val)}
              >
                {legOptions.map(lg => (
                  <Option key={lg} value={lg}>{lg}</Option>
                ))}
              </Select>
            </Col>
          </Row>

          {/* Row 4: Notes */}
          <Row style={{ marginBottom: "16px" }}>
            <Col span={24}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "#1A354D", marginBottom: "4px" }}>
                Notes
              </label>
              <TextArea
                rows={3}
                placeholder="Enter Notes"
                style={{ width: "100%" }}
                value={row.notes}
                onChange={e => handleChangeRow(index, "notes", e.target.value)}
              />
            </Col>
          </Row>

          {/* Row 5: Upload Image */}
          <Row>
            <Col span={24}>
              <Upload
                className="osd-upload-list"
                beforeUpload={() => false}
                multiple
                maxCount={5}
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                fileList={row.fileList || []}
                showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                onChange={info => handleChangeRow(index, "fileList", info.fileList)}
                onPreview={file => {
                  const src = file.url || file.thumbUrl || (file.originFileObj && URL.createObjectURL(file.originFileObj));
                  if (src) window.open(src, "_blank");
                }}
              >
                <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: "#586fcc", cursor: "pointer", fontWeight: 500 }}>
                  <Icon type="camera" theme="filled" style={{ fontSize: "16px" }} />
                  <span>Upload Image{row.fileList?.length > 0 ? ` (${row.fileList.length})` : ""}</span>
                </div>
                {row.fileList?.length > 0 && (
                  <span style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    background: "#586fcc",
                    color: "white",
                    borderRadius: "8px",
                    minWidth: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: 500
                  }}>+{row.fileList.length}</span>
                )}
              </Upload>
            </Col>
          </Row>
        </div>
      ))}
      {errorMsz?.length > 0 && (
        <div
          style={{
            border: "1px solid #ff6257",
            padding: "14px",
            background: "#FFF1F0",
            borderRadius: "4px",
            marginTop: "12px"
          }}
        >
          {errorMsz.map((error, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: i !== errorMsz.length - 1 ? "8px" : 0
              }}
            >
              <Icon
                type="close-circle"
                style={{ color: "#d63941", fontSize: "16px", marginRight: "8px" }}
              />
              <span>{error}</span>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default AddOSDModal;
